import {
  createInvoicePreviewUrl,
  createUpoPreviewUrl,
  revokePreviewUrl,
} from './api';
import { AdditionalData, DocumentKind, XmlInput } from './types';

/**
 * `<ksef-pdf-preview>` — drop-in podgląd PDF dla czystego HTML (i każdego
 * frameworka przez custom elements). Renderuje fakturę KSeF / UPO z XML-a
 * wewnątrz <iframe> (natywny renderer PDF przeglądarki, bez pdf.js).
 *
 * Użycie:
 *   <ksef-pdf-preview nr-ksef="..." watermark="PODGLĄD"></ksef-pdf-preview>
 *   el.xml = '<Faktura>...</Faktura>';     // albo
 *   el.file = inputEl.files[0];            // File/Blob
 *
 * Atrybuty: nr-ksef, watermark, type ("invoice" | "upo").
 * Property:  file (File|Blob), xml (string), data (AdditionalData — nadpisuje atrybuty).
 * Zdarzenia: "rendered" (detail.url), "error" (detail.error), "loading".
 */
export class KsefPdfPreviewElement extends HTMLElement {
  static readonly tagName = 'ksef-pdf-preview';
  static get observedAttributes(): string[] {
    return ['nr-ksef', 'watermark', 'type'];
  }

  #input: XmlInput | null = null;
  #dataOverride: Partial<AdditionalData> | null = null;
  #url: string | null = null;
  #renderToken = 0;
  #iframe!: HTMLIFrameElement;
  #status!: HTMLDivElement;

  // --- API właściwości ---

  /** Ustaw plik/Blob XML. */
  set file(value: File | Blob | null) {
    this.#input = value;
    this.#scheduleRender();
  }

  /** Ustaw treść XML jako string. */
  set xml(value: string | null) {
    this.#input = value;
    this.#scheduleRender();
  }

  /** Nadpisz dane dodatkowe (nrKSeF, qrCode, watermark, ...). */
  set data(value: Partial<AdditionalData> | null) {
    this.#dataOverride = value;
    this.#scheduleRender();
  }

  get type(): DocumentKind {
    return (this.getAttribute('type') as DocumentKind) || 'invoice';
  }
  set type(value: DocumentKind) {
    this.setAttribute('type', value);
  }

  // --- Cykl życia ---

  connectedCallback(): void {
    if (!this.shadowRoot) this.#mount();
    this.#scheduleRender();
  }

  disconnectedCallback(): void {
    this.#release();
  }

  attributeChangedCallback(): void {
    if (this.shadowRoot) this.#scheduleRender();
  }

  // --- Wewnętrzne ---

  #mount(): void {
    const root = this.attachShadow({ mode: 'open' });
    root.innerHTML = `
      <style>
        :host { display: block; position: relative; width: 100%; height: 100%; min-height: 320px; }
        iframe { width: 100%; height: 100%; border: 0; display: block; background: #fff; }
        .status {
          position: absolute; inset: 0; display: none; place-items: center;
          font: 14px/1.4 system-ui, sans-serif; color: #555; padding: 1rem; text-align: center;
          background: #fafafa;
        }
        .status[data-show] { display: grid; }
        .status[data-kind="error"] { color: #b00020; }
      </style>
      <iframe title="Podgląd PDF KSeF" part="iframe"></iframe>
      <div class="status" part="status"></div>
    `;
    this.#iframe = root.querySelector('iframe')!;
    this.#status = root.querySelector('.status')!;
  }

  #scheduleRender(): void {
    if (!this.isConnected || !this.shadowRoot) return;
    queueMicrotask(() => this.#render());
  }

  #buildData(): AdditionalData {
    return {
      nrKSeF: this.getAttribute('nr-ksef') ?? 'PODGLĄD',
      watermark: this.getAttribute('watermark') ?? undefined,
      ...this.#dataOverride,
    };
  }

  async #render(): Promise<void> {
    if (!this.#input) {
      this.#showStatus('Brak danych XML do wyświetlenia.', 'info');
      return;
    }
    const token = ++this.#renderToken;
    this.#showStatus('Generowanie PDF…', 'info');
    this.dispatchEvent(new CustomEvent('loading'));

    try {
      const url =
        this.type === 'upo'
          ? await createUpoPreviewUrl(this.#input)
          : await createInvoicePreviewUrl(this.#input, this.#buildData());

      // Inny render wystartował w międzyczasie — porzuć ten wynik.
      if (token !== this.#renderToken) {
        revokePreviewUrl(url);
        return;
      }
      this.#release();
      this.#url = url;
      this.#iframe.src = url;
      this.#hideStatus();
      this.dispatchEvent(new CustomEvent('rendered', { detail: { url } }));
    } catch (error) {
      if (token !== this.#renderToken) return;
      this.#showStatus('Nie udało się wygenerować PDF.', 'error');
      this.dispatchEvent(new CustomEvent('error', { detail: { error } }));
    }
  }

  #showStatus(text: string, kind: 'info' | 'error'): void {
    this.#status.textContent = text;
    this.#status.dataset.kind = kind;
    this.#status.setAttribute('data-show', '');
  }

  #hideStatus(): void {
    this.#status.removeAttribute('data-show');
  }

  #release(): void {
    revokePreviewUrl(this.#url);
    this.#url = null;
  }
}

/** Rejestruje element (idempotentnie). Wywoływane automatycznie przy imporcie tego wejścia. */
export function defineKsefPdfPreview(tagName = KsefPdfPreviewElement.tagName): void {
  if (typeof customElements !== 'undefined' && !customElements.get(tagName)) {
    customElements.define(tagName, KsefPdfPreviewElement);
  }
}

defineKsefPdfPreview();

declare global {
  interface HTMLElementTagNameMap {
    'ksef-pdf-preview': KsefPdfPreviewElement;
  }
}
